// The VPC IPAM copy that must not drift.
//
// These strings were spelled once in IpamBlockedPanel.js and then hand-retyped in
// IpamEntry.js, IpamAccounts.js and IpamPoolDetail.js — three of the four with a
// TYPOGRAPHIC apostrophe instead of the deliberate ASCII one. Nothing looked
// wrong: the wording matched, so no reviewer caught it. What broke was the
// verification step, which greps for these sentences verbatim and was therefore
// finding one occurrence out of four, and the constants themselves, which could
// be edited while three screens went on saying the old thing.
//
// So they live here, in a module that owns nothing else. Any screen that states
// what teardown does to a customer's network, or what it costs them, imports from
// this file. It never retypes.
//
// The apostrophes are ASCII on purpose. See IPAM_NO_VPC_DELETED.

// THE TWO LINES THAT MUST APPEAR VERBATIM, on the pre-flight ledger and again
// inside the confirmation dialog. They answer the two questions an operator
// actually has, and they answer them in opposite directions: the first is the
// reassurance that is TRUE (nothing in your network is touched), the second is
// the loss that is PERMANENT (the history is gone). Softening either one is what
// turns an informed decision into a surprise.
// The apostrophe is deliberately the plain ASCII one: this sentence is quoted
// verbatim in the spec and the plan and is grepped for by the verification step,
// and a typographic apostrophe would make an exact-string check miss it.
export const IPAM_NO_VPC_DELETED =
  "No VPC is deleted. Your VPCs keep their addresses; only IPAM's record of them goes away.";

export const IPAM_HISTORY_DESTROYED =
  'All CIDR address history is destroyed, irreversibly.';

// THE BILLING SENTENCE, AND THE ONE IT MUST NEVER BE REPLACED BY. Whether the
// hourly meter prorates is AWS's behaviour and Prism has not verified it, so the
// promise Prism can keep is that billing stops when AWS reports the delete — not
// that "charges stop immediately", which is a promise no code here can honour.
export const IPAM_BILLING_STOPS =
  'Billing stops when AWS reports the IPAM deleted. Prism does not promise charges stop immediately.';

export const IPAM_STILL_BILLING =
  'The IPAM is still running and still billing until it is deleted.';

// The product boundary, stated as a promise rather than an apology: Prism holds
// write credentials in these accounts and deliberately does not use them here.
export const IPAM_NEVER_DELETES_YOUR_RESOURCES =
  'Prism will never delete a resource in your account.';

// The instruction, with the delay named. "Delete the VPC and retry" without the
// wait produces a second identical refusal and reads as a broken button.
export const IPAM_ALLOCATION_RELEASE_DELAY =
  'IPAM releases the allocation automatically several minutes after the VPC is deleted — it is not '
  + 'instant. Delete the VPC (or release its CIDR), wait for IPAM to catch up, then press Retry teardown.';

// What force delete actually does, from the SDK's own description of
// DeleteIpam(Cascade=true). It names both costs: the history loss and the
// deallocation from live VPCs.
export const IPAM_FORCE_DELETE_COPY =
  'Force delete runs DeleteIpam --cascade. It deallocates CIDRs from live VPC resources — no VPC '
  + 'resource is deleted and no address changes, IPAM simply stops tracking them — deprovisions the '
  + 'CIDRs from every private-scope pool, deletes those pools and scopes, and then deletes the IPAM. '
  + 'All CIDR address history is destroyed, irreversibly, with or without force delete.';

// The reason a disabled Force delete carries with it. A disabled control that
// explains itself beats an AWS error the operator has to decode, and it is what
// keeps force delete from ever reading as a first action.
export const IPAM_CASCADE_NOT_YET_REASON =
  'Force delete is offered only after the ordered teardown has stopped and asked for it. No teardown '
  + 'is parked on that decision, so run the ordered teardown first.';

export const IPAM_CASCADE_UNREADABLE_REASON =
  'Prism could not read the live teardown job, so it cannot tell whether the ordered teardown has '
  + 'asked for a force delete. Force delete stays disabled until it can.';

// The confirm button's label. Naming the benefit is what makes the destructive
// verb acceptable — "Delete" alone asks the operator to authorise a loss with no
// statement of what they get for it.
export const IPAM_DESTRUCTIVE_CONFIRM_LABEL = 'Delete the IPAM and stop the meter';

// The teardown job's non-terminal statuses. `awaiting_cascade` is the only one
// from which the backend accepts a force delete, and that precondition is not
// ceremony: reaching it means the drain gate did not open, or failed-deprovision
// outlasted its retry ladder, or the graceful budget expired — each a state
// cascade is known to clear and nothing else is.
export const IPAM_TEARDOWN_AWAITING_CASCADE = 'awaiting_cascade';
export const IPAM_TEARDOWN_BLOCKED = 'blocked';

// --- WHY A REFRESH CONTROL IS OFF — AND THE ONE CASE WHERE IT MUST NOT BE ----
//
// A control gated on the IPAM config row has THREE answers, not two, and the
// third was the one nobody wrote: the capability is live, the capability is
// DECIDEDLY not live, or PRISM COULD NOT FIND OUT. `!ipamIsLive(config?.status)`
// collapses the last two — a GET /config that rejected, or that has not landed
// yet, leaves `status` undefined, which the predicate reads as "not live". What
// the operator gets is a button that still says "Refresh", is permanently dead,
// sends no request, raises no error and explains nothing. That is an absence
// rendered as a confident negative, and it is rendered ON THE ONE CONTROL that
// would have let them find out otherwise.
//
// The reasons live here rather than on the pages for the reason IPAM_CASCADE_*
// does: the pattern already exists in this capability — IpamBlockedPanel pairs a
// disabled Force delete with an Alert that separates "no teardown has asked for
// it" from "Prism could not read the job" — two more screens need it now, and a
// reason that gets retyped is a reason that drifts.
//
// A DISABLED CONTROL THAT DOES NOT SAY WHY IS THE DEFECT, so every arm below is
// a rendered sentence and none of them is a fallback for the others.

// Decided, and the status is the whole answer. Takes an ALREADY-FORMATTED label
// so this module keeps its zero imports; callers pass formatStatusLabel(status).
export const ipamRefreshNotLiveReason = (stateLabel) =>
  `Refresh is off because this customer's Amazon VPC IPAM is "${stateLabel}". A discovery refresh `
  + 'is accepted only while the IPAM is enabled — the service refuses every other status — so '
  + 'pressing it would re-read nothing.';

// Decided, from the config route's own contract: GET /ipam/config answers 200
// with a null body for a tenant that has never configured IPAM, so a null row on
// a SUCCESSFUL read is a real answer and not a missing one.
export const IPAM_REFRESH_NOT_CONFIGURED_REASON =
  'Refresh is off because Amazon VPC IPAM is not set up for this customer, so there is no discovery '
  + 'sweep to re-read. Enable the capability first.';

// Not decided, but self-resolving: the read is in flight. Said out loud anyway,
// because a control that is off for a second with no explanation and a control
// that is off forever with no explanation look identical.
export const IPAM_REFRESH_PENDING_REASON =
  'Refresh is off for a moment while Prism reads this customer’s VPC IPAM configuration. It '
  + 'switches itself on as soon as that read lands.';

// THE THIRD STATE, AND THE ONE THAT LEAVES THE BUTTON CLICKABLE.
//
// Prism does not know whether a refresh would be accepted, and "does not know"
// must not be spent as "no". The service is already the authority on this exact
// predicate — StartRefresh answers "cannot refresh while VPC IPAM is <status>" —
// so the honest move is to let the click reach it: it either starts a sweep or
// returns a real sentence into the page's error Alert, and the same handler
// re-reads the configuration on its way through, which makes the button its own
// retry for the read that failed.
export const IPAM_REFRESH_UNREADABLE_REASON =
  'Prism could not read this customer’s VPC IPAM configuration, so it cannot tell whether a '
  + 'refresh would be accepted — which is not the same as knowing it would not. The button is '
  + 'left live on purpose: pressing it asks the service, which is the authority, and Prism re-reads '
  + 'the configuration at the same time.';

// The busy arm. Not a state of the config at all, but it is the fourth thing a
// greyed-out Refresh can mean and it was equally unexplained.
export const IPAM_REFRESH_RUNNING_REASON = 'A refresh is already running.';

// --- The Address space zero state -------------------------------------------
//
// "AWS REPORTED NO CIDRs" IS A CLAIM, AND PRISM SPENT IT ON A STATE IT HAD NOT
// ESTABLISHED. Live, POC tenant, 2026-08-23, on a clean first-ever enable: the
// page said
//
//   0 CIDRs · 0 overlapping of 0 VPCs
//   "No address space discovered yet
//    AWS reported no CIDRs in the 2 accounts x 1 region currently in scope,
//    as of 23/08/2026, 02:01:04."
//
// while ipam_discovered_cidrs held 14 rows. AWS had reported 14 CIDRs and Prism
// had stored every one of them. What was empty was ipam_scope_cidrs — the
// PER-SCOPE view, which AWS populates only after the per-discovery view has
// landed, verified against live AWS at that same moment (both scopes 0, both
// discoveries 7). The list was driven by the scope table alone, so the whole
// screen collapsed to a timestamped falsehood about AWS.
//
// This is the codebase's documented recurring pattern — an unread or partial
// state rendered as a confident negative while every log reads healthy — and the
// fix is not to reword the sentence but to MAKE ROOM FOR THE THIRD CASE. The
// service now returns both tenant-wide totals, and the four arms below are
// mutually exclusive, none of them a fallback for the others:
//
//   more pages remain -> not a zero at all, just the end of what is loaded
//   totals unknown    -> Prism cannot tell; it says so instead of guessing
//   scope view behind -> AWS reported plenty; the scope view has not caught up
//   both totals zero  -> the ONLY state in which the original sentence is true
//
// It lives here, not on the page, for the reason this whole module exists: the
// same claim is hand-typed on three sibling screens with three different verbs
// ("AWS reported no CIDRs", "AWS reported no pool allocations", "AWS discovered
// no public IPv4 addresses"), which is exactly the drift the header warns about.
// A screen that states what AWS reported imports it.

const ipamPlural = (n, one, many) => (n === 1 ? one : many);

// The shared "Prism has not looked" body. One sentence per page differs — the
// noun — and the rest must not, because three hand-typed variants of this
// paragraph is precisely the drift this module exists to stop.
//
// It is declared HERE, above the first caller, rather than beside the two
// per-cache zero states that were its first users: the Address space zero state
// needs it too and a helper defined below its callers reads as an accident.
// The headline for "Prism has not looked", shared with IpamDiscoveryColdStart —
// which is where it was first spelled, and which now imports it from here.
export const IPAM_NEVER_READ_TITLE = 'Prism has not read your address space yet';

const ipamNeverReadBody = (noun) =>
  `No inventory refresh has completed the ${noun} read for this tenant, so nothing on this page is a `
  + 'statement about what AWS holds — Prism has not established it. Other parts of the inventory may '
  + 'well have been read; they are separate reads and this one has not landed. Run a refresh; if it '
  + 'keeps completing without reading this list, the refresh itself is what needs looking at, not AWS.';

// ipamAddressSpaceZeroState picks the empty-table copy.
//
// discoveredTotal / scopeTotal are the UNFILTERED tenant-wide counts from the
// page envelope, and null means "the reply did not carry them" — which is a
// third value, not a zero. Everything else is display context.
//
// THE TOTALS ARE NOT ENOUGH, AND THAT WAS THE SECOND HALF OF THIS BUG. Both of
// them are COUNTS OVER PRISM'S OWN TABLES, so `scopeTotal === 0` is equally the
// answer for "AWS reported nothing" and for "the scope read never landed, so
// nothing was ever written". The discriminator is the server's per-cache
// freshness stamp — models.IpamConfig.ScopeCidrsReadAt / DiscoveredCidrsReadAt,
// each set only by a run in which EVERY read feeding that cache completed. Null
// means the cache has never been read whole, which is a third value and never a
// zero: with both reads failing while any other cache yielded a row, this
// function used to print "AWS reported no CIDRs … as of <time>" — a claim about
// AWS, with a timestamp, built out of two reads that never happened.
export const ipamAddressSpaceZeroState = ({
  discoveredTotal = null,
  scopeTotal = null,
  scopeCidrsReadAt = null,
  discoveredCidrsReadAt = null,
  accountCount = 0,
  regionCount = 0,
  someAccountsBroken = false,
  lastRefreshed = '',
  morePagesRemain = false,
} = {}) => {
  if (morePagesRemain) {
    return {
      title: 'No CIDRs on the pages loaded so far',
      description: 'More pages are still waiting to be read. Use "Load more" below before concluding '
        + 'that this IPAM has no address space — nothing here is a zero yet.',
    };
  }

  // THE THREE UNREAD ARMS, AND THEY COME BEFORE EVERY ARM THAT READS A TOTAL.
  // Each names the read that has not landed, because "Prism has not looked" and
  // "Prism looked at the wrong half" send an operator to different places.
  if (!discoveredCidrsReadAt && !scopeCidrsReadAt) {
    return {
      // THE SAME SENTENCE THE COLD-START PANEL SHOWS, AND THEREFORE THE SAME
      // CONSTANT. IpamDiscoveryColdStart reaches this claim from a different
      // discriminator — no refresh has EVER completed for the tenant — but it is
      // the same claim to the reader, and two definitions of one sentence is the
      // drift this module exists to stop. The BODIES differ, because the evidence
      // does; the headline does not.
      title: IPAM_NEVER_READ_TITLE,
      description: ipamNeverReadBody('address-space'),
    };
  }

  if (!discoveredCidrsReadAt) {
    return {
      title: "Prism has not read AWS's discovered CIDRs yet",
      description: ipamNeverReadBody('discovered-CIDR'),
    };
  }

  if (!scopeCidrsReadAt) {
    return {
      title: 'Prism has not read the IPAM scope view yet',
      description: ipamNeverReadBody('scope-CIDR'),
    };
  }

  if (typeof discoveredTotal !== 'number' || typeof scopeTotal !== 'number') {
    return {
      title: 'Prism cannot say whether AWS reported any address space',
      description: 'This list came back empty, but the reply did not carry the totals Prism needs to tell '
        + 'an honest zero from a scope view that has not caught up. Treat this as unknown rather than as '
        + '"nothing found", and refresh.',
    };
  }

  if (discoveredTotal > 0 && scopeTotal === 0) {
    return {
      title: 'AWS has not populated the IPAM scope view yet',
      description: `Prism holds ${discoveredTotal} `
        + `${ipamPlural(discoveredTotal, 'CIDR', 'CIDRs')} that AWS's resource discoveries have already `
        + 'reported. AWS populates the per-scope view — the source of the Compliance, Overlap and '
        + 'Management columns — only after a discovery sweep has landed, and it is still empty for this '
        + 'IPAM. This is not a report that AWS found no address space: it is the scope view running '
        + 'behind discovery, which it normally clears within a few minutes of the first sweep.',
    };
  }

  return {
    title: 'No address space discovered yet',
    description: `AWS reported no CIDRs in the ${accountCount} `
      + `${ipamPlural(accountCount, 'account', 'accounts')}`
      + `${someAccountsBroken ? ' still discovering' : ''} × `
      + `${regionCount} ${ipamPlural(regionCount, 'region', 'regions')} `
      + `currently in scope, as of ${lastRefreshed || 'the last refresh'}.`,
  };
};

// ipamScopeViewLagNotice is the banner for the case the zero state above no
// longer reaches: rows ARE on screen, and some of them are CIDRs AWS's
// discoveries reported that its per-scope view has not.
//
// It returns null when every loaded row has been reported, because a permanent
// caveat is a caveat nobody reads. `reported`/`unreported` are counted over the
// rows actually loaded — the honest denominator — and scopeTotal decides only
// whether the strong form ("the scope view is empty") may be used.
export const ipamScopeViewLagNotice = ({ unreported = 0, reported = 0, scopeTotal = null } = {}) => {
  if (unreported <= 0) return null;

  const columns = 'Compliance, Overlap and Management';
  if (reported === 0 && scopeTotal === 0) {
    return {
      title: 'AWS has not reported any of this address space in the IPAM scope view yet',
      description: `All ${unreported} ${ipamPlural(unreported, 'CIDR', 'CIDRs')} below come from AWS's `
        + `resource discoveries. The per-scope view — the only source of the ${columns} columns — has `
        + 'reported none of them yet; AWS populates it after a discovery sweep lands, so it trails '
        + `discovery on a newly enabled IPAM. Those three columns read as unknown on every row below, `
        + 'which is not the same as clean.',
    };
  }
  return {
    title: `${unreported} of the ${unreported + reported} `
      + `${ipamPlural(unreported + reported, 'CIDR', 'CIDRs')} loaded are not in the scope view yet`,
    description: `AWS's resource discoveries have reported these prefixes and its per-scope view — the `
      + `only source of the ${columns} columns — has not caught up with them. Those three columns read `
      + 'as unknown on those rows. An unknown posture is not a clean one, and these rows are not '
      + 'counted in the overlap figure above.',
  };
};

// --- THE PER-CACHE THIRD STATE ------------------------------------------------
//
// "AWS discovered no public IPv4 addresses" and "AWS reported no pool
// allocations" are the two remaining sentences in this capability that state what
// AWS said. Both were gated on `config.firstDiscoveryAt`, which is ONE AGGREGATE
// OVER FIVE CACHES: the server stamps it from ipam_refresh_jobs.rows_fetched, a
// single total across the discovered-account, discovered-CIDR, scope-CIDR,
// public-address and pool-allocation ingests. Any one of them producing a row
// disarms the cold-start panel on all seven pages — so a tenant that merely had
// discovered CIDRs got the ORDINARY empty state on these two, and the ordinary
// empty state is a claim about AWS.
//
// Live, POC tenant, 2026-08-23, both fired on a tenant whose public-address and
// pool-allocation caches had never been successfully read. Same shape as the
// Address space falsehood above; same fix — MAKE ROOM FOR THE THIRD CASE rather
// than reword the sentence.
//
// The server now carries one timestamp PER CACHE
// (models.IpamConfig, the per-cache freshness block), each stamped only by a run
// in which every read feeding that cache completed. Three states, and only the
// last may say what AWS reported:
//
//   absent            Prism has never read this cache whole. Nothing on the page
//                     is a statement about AWS.
//   set, rows present not a zero at all
//   set, no rows      read, and genuinely empty. The sentence is true.
//
// `cacheReadAt` below is that timestamp and null means ABSENT — a third value,
// never a zero. Both functions return {title, description} exactly as
// ipamAddressSpaceZeroState does, so the three screens stay interchangeable.

// The shared "Prism has not looked" body is `ipamNeverReadBody`, declared above
// ipamAddressSpaceZeroState because that function is now its first caller too.

// ipamPublicIpsZeroState picks the empty-table copy for Public IP Insights.
//
// brokenCount / discoveringCount / scopeSentence / asOf are display context and
// carry the live-validated wording of the honest zero unchanged. cacheReadAt is
// the discriminator and is the only new input.
export const ipamPublicIpsZeroState = ({
  cacheReadAt = null,
  morePagesRemain = false,
  brokenCount = 0,
  discoveringCount = 0,
  scopeSentence = 'the accounts and regions currently in scope',
  asOf = '',
} = {}) => {
  if (morePagesRemain) {
    return {
      title: 'No public IPv4 addresses on the pages loaded so far',
      description: 'More pages are still waiting to be read. Use "Load more addresses" below before '
        + 'concluding that this estate holds none — nothing here is a zero yet.',
    };
  }

  if (!cacheReadAt) {
    return {
      title: 'Prism has not read your public IPv4 addresses yet',
      description: ipamNeverReadBody('public-address'),
    };
  }

  if (brokenCount > 0) {
    return {
      title: `AWS discovered no public IPv4 addresses in the ${discoveringCount} `
        + `${ipamPlural(discoveringCount, 'account', 'accounts')} that are still discovering`,
      description: `This says nothing about the ${brokenCount} `
        + `${ipamPlural(brokenCount, 'account', 'accounts')} named in the warning above — nothing is being `
        + `discovered there. Last sweep: ${asOf || 'not recorded'}.`,
    };
  }

  return {
    title: `AWS discovered no public IPv4 addresses in ${scopeSentence}`,
    description: `As of ${asOf || 'the last sweep, whose time was not recorded'}. Addresses allocated `
      + 'since then appear on the next sweep.',
  };
};

// ipamAllocationsZeroState picks the empty-table copy for the pool-allocation
// ledger.
//
// The honest-zero arm keeps its second, entirely legitimate reading: pools may
// exist with nothing allocated out of them, which is a normal state and not a
// gap. That reading is only available once the cache HAS been read — before
// that, "nothing allocated" and "nobody looked" are the same empty table.
export const ipamAllocationsZeroState = ({
  cacheReadAt = null,
  morePagesRemain = false,
  accountCount = 0,
  regionCount = 0,
  someAccountsBroken = false,
  lastRefreshed = '',
} = {}) => {
  if (morePagesRemain) {
    return {
      title: 'No allocations on the pages loaded so far',
      description: 'More pages are still waiting to be read. Load the rest before concluding that '
        + 'nothing has been allocated from a pool.',
    };
  }

  if (!cacheReadAt) {
    return {
      title: 'Prism has not read your pool allocations yet',
      description: ipamNeverReadBody('pool-allocation'),
    };
  }

  return {
    title: 'No CIDRs have been allocated from a pool',
    description: `AWS reported no pool allocations in the ${accountCount} `
      + `${ipamPlural(accountCount, 'account', 'accounts')}`
      + `${someAccountsBroken ? ' still discovering' : ''} × `
      + `${regionCount} ${ipamPlural(regionCount, 'region', 'regions')} `
      + `currently in scope, as of ${lastRefreshed || 'the last refresh'}. `
      + 'Pools can exist with nothing allocated out of them yet — check Address space to see the CIDRs '
      + 'that exist without a pool behind them.',
  };
};

// --- THE METERED ACTIVE-IP FIGURE, AND WHERE IT COMES FROM --------------------
//
// TWO NUMBERS, TWO MEANINGS, ONE SCREEN. The dashboard shows the discovered
// address space (what IPAM has inventoried) a few centimetres above the metered
// active-IP count (what AWS bills on), and the caption under the second one said
//
//   "Counted from the address space AWS has discovered so far"
//
// which is the provenance of the FIRST one. The figure is
// ipam_configs.estimated_active_ips, and that column is written by
// estimateActiveIPs from DescribeNetworkInterfaces — attached ENIs, in the
// accounts and Regions the customer enrolled. It has never had anything to do
// with the discovered address space, and the two routinely differ by a lot: a
// /16 discovered as one CIDR is one row of address space and up to 65k billable
// addresses, while an ENI in an un-enrolled Region is billed and never
// discovered.
//
// AND IT CARRIES ITS AGE, ALWAYS. The figure was computed once at enable and
// never again until the refresh started re-counting it; the panel meanwhile
// promised "what VPC IPAM is billing you for right now". The re-count can fail
// on its own without failing the refresh, in which case the previous figure
// stands — so the age is not decoration, it is the only thing separating a
// re-counted number from one nobody has been able to re-read for a fortnight.

export const IPAM_METERED_SUBTITLE = 'What AWS meters — attached ENIs, as last counted by Prism';

// THE OTHER HALF OF THE PAIR. The dashboard shows both numbers, so the
// distinction has to be stated on the discovered-CIDR tile as well — a caption
// that is only correct on one of two adjacent figures is how they got confused in
// the first place.
export const IPAM_ADDRESS_SPACE_VS_METER =
  'This is NOT the metered active-IP figure in the cost panel. That one counts attached ENIs '
  + '(ec2:DescribeNetworkInterfaces), which is what AWS bills on; a single discovered /16 is one CIDR '
  + 'here and up to 65,536 billable addresses there.';

export const IPAM_ACTIVE_IPS_TILE_LABEL = 'Active IPs on the meter';

// ipamMeteredCaption is the sentence under the two cost tiles.
//
// price is the formatted rate string; countedRel is an already-rendered relative
// time ("3 min ago") or null when Prism has no record of when it counted.
// activeIps is null when there is no figure at all.
export const ipamMeteredCaption = ({ activeIps = null, price = '', countedRel = null } = {}) => {
  if (activeIps === null) {
    return `Prism could not count your active IPs. The charge is real regardless — VPC IPAM Advanced `
      + `tier is billed at ${price}.`;
  }
  const age = countedRel
    ? `Counted ${countedRel}`
    : 'Prism has no record of when this was counted';
  return `Advanced tier is billed at ${price}. Counted from attached ENIs — `
    + 'ec2:DescribeNetworkInterfaces in the accounts and Regions you enrolled — which is what AWS '
    + 'meters, and is a lower bound. This is NOT the discovered address space shown above: that is '
    + `what IPAM has inventoried, this is what is billable, and they do not match. ${age}; it is `
    + "re-counted by a full refresh, not live. AWS's own meter may differ.";
};

// --- WHOSE ABSENCE IS IT? — THE PER-CACHE STAMPS ON THE OTHER FIVE SCREENS ----
//
// The freshness block on models.IpamConfig carries FIVE stamps and, until this
// change, TWO of them had a reader. `scopeCidrsReadAt`, `discoveredCidrsReadAt`
// and `discoveredAccountsReadAt` were written by the server every refresh and
// consumed by nobody, so three screens went on discriminating "AWS reported
// nothing" from "AWS reported something" on ROW COUNTS alone — and a row count
// over a cache nothing ever wrote is not a measurement of AWS.
//
// The sentences below are the third arm each of those screens was missing. They
// all say the same thing in the screen's own vocabulary: THE SUBJECT OF THE
// ABSENCE IS PRISM, NOT AWS. "AWS has never completed a discovery sweep for this
// account" and "Prism has never read AWS's account list" are both true-sounding
// and only one of them is established by an empty table.

export const IPAM_ACCOUNT_DISCOVERY_UNREAD_LABEL = 'Prism: not read';

export const IPAM_ACCOUNT_DISCOVERY_UNREAD_HINT =
  'Prism has never completed a read of AWS\'s discovered-account list for this tenant, so it holds '
  + 'no AWS row for this account. That is not AWS reporting nothing about it — nothing has been '
  + 'read. Run a discovery refresh; until one completes this column is unknown, not healthy and '
  + 'not empty.';

export const IPAM_ACCOUNT_SWEEP_UNREAD_HINT =
  'Prism has never completed a read of AWS\'s discovered-account list, so it does not know when — '
  + 'or whether — AWS last swept this account. This is unknown, not "never".';

// The Configuration card's "Last discovery sweep" row. The value behind it is
// `lastDiscoveryRefreshAt`, which is PRISM'S OWN refresh clock and not AWS's
// sweep clock — the sibling cold-start panel renders the same field as "Prism
// last read AWS". Naming AWS as the subject of the absence pointed every
// operator who read it at the wrong service.
export const IPAM_TENANT_NEVER_REFRESHED =
  'Prism has never completed a discovery refresh for this tenant.';

// The dashboard's third arm, beside STATE B. STATE B says "AWS's last sweep
// found no address space", which requires a sweep Prism has actually read.
export const IPAM_DASHBOARD_INVENTORY_UNREAD_TITLE =
  'Prism has not read this inventory yet';

export const IPAM_DASHBOARD_INVENTORY_UNREAD_BODY = ipamNeverReadBody('inventory');

// The Public IPs tile, when its own cache has never been read whole. StatTile
// draws `null` as a muted em-dash, and this replaces its default "could not read
// this rollup" caption — the rollup read fine, it is the CACHE BEHIND IT that
// has never been filled.
export const IPAM_PUBLIC_IPS_TILE_UNREAD_CAPTION = 'Not read yet — no completed public-address read';

export const IPAM_PUBLIC_IPS_TILE_UNREAD_HINT =
  'No inventory refresh has completed the public-address read for this tenant, so Prism holds no '
  + 'public IPv4 addresses to count. A zero here would read as "you have no public IPs", which '
  + 'nothing has established. Run a refresh.';

// --- ADOPTION CHANGES WHAT TEARDOWN DOES, AND THEREFORE WHAT IT MAY PROMISE ---
//
// Prism NEVER deletes an IPAM it adopted (services/ipam_teardown.go builds a
// `critical` residue row saying exactly that, and sets StillBilling = true). So
// on an adopted IPAM all three of this module's teardown promises are false:
// the IPAM is not deleted, its history is not destroyed, and the meter does not
// stop. IpamSetup already said the correct thing forty lines above the alert
// that said the opposite — the same screen, contradicting itself.
//
// The constants below are the adopted spellings, and the three helpers pick
// between them. They take the flag rather than reading it themselves so this
// module keeps its zero imports; callers pass `config.ipamAdopted` (the past
// tense) or `preflight.hostIpamDisposition === 'adoptable'` (the future one).

// THE SENTENCE, spelled once. Every screen that tells an operator what turning
// VPC IPAM off does to an ADOPTED IPAM renders this and does not retype it.
export const IPAM_ADOPTED_TEARDOWN_SUMMARY =
  "Turning VPC IPAM off removes Prism's associations. The IPAM is not deleted, its history is not "
  + 'destroyed, and its meter keeps running.';

export const IPAM_ADOPTED_HISTORY_KEPT =
  'No CIDR address history is destroyed. Prism never deletes an IPAM it adopted, so AWS keeps the '
  + 'history with it.';

export const IPAM_ADOPTED_STILL_METERING =
  'Billing does not stop. Prism never deletes an IPAM it adopted — the IPAM and its Advanced-tier '
  + 'meter keep running after this teardown, and only Prism stops using it. Delete it yourself in '
  + "the host account's VPC IPAM console if you want the meter to stop.";

export const IPAM_ADOPTED_CONFIRM_LABEL = 'Turn VPC IPAM off — the IPAM keeps running';

export const ipamHistoryDestroyed = (adopted = false) =>
  (adopted ? IPAM_ADOPTED_HISTORY_KEPT : IPAM_HISTORY_DESTROYED);

export const ipamBillingStops = (adopted = false) =>
  (adopted ? IPAM_ADOPTED_STILL_METERING : IPAM_BILLING_STOPS);

// The confirm button's label. "Delete the IPAM and stop the meter" is a promise
// of two things an adopted teardown does neither of.
export const ipamDestructiveConfirmLabel = (adopted = false) =>
  (adopted ? IPAM_ADOPTED_CONFIRM_LABEL : IPAM_DESTRUCTIVE_CONFIRM_LABEL);

// The pre-flight's own verdict, which the danger zone ignored entirely.
// `stillBilling` is a FIELD on IpamTeardownPreview precisely so the client does
// not have to derive it — "a client that forgot to derive it would render a
// reassuring page over a live meter" — and this page forgot to READ it.
//
// `stillBilling` is undefined when the ledger has not loaded: a third value, and
// it must not be spent as "no".
//
// `verified` is IpamSummary.billingVerified / IpamCostPreview.billingVerified /
// IpamTeardownPreview.BillingVerified, and it is a SECOND, INDEPENDENT AXIS. It
// says nothing about which way the meter is running; it says whether Prism can
// confirm the verdict from anything current.
//
// FALSE HAS THREE BASES AND THE COPY MAY PRESUPPOSE NONE OF THEM. The verdict may
// rest on a row Prism has STOPPED refreshing (the capability is disabled, so no
// discovery pass will ever run again); on a ledger read that FAILED; or on a
// ledger holding NO `ipam` rows at all — a tenant Prism never recorded, or an
// enable that crashed before it wrote one (services/ipam_service.go, meterVerdict:
// all three fall back to cfg.Status and set Verified=false).
//
// In the first case a sighting exists and is merely old. IN THE OTHER TWO THERE
// WAS NEVER A SIGHTING. So "the last reading Prism took" and "the reading rests
// on state Prism has stopped refreshing" are both UNSAFE here: they assert a
// reading happened, which is this capability's signature defect — unread state
// presented as a fact — wearing the clothes of the fix for it. The wording below
// is basis-neutral on purpose: it says only that Prism cannot confirm the meter
// from anything current, which is true of all three.
//
// Where Prism DOES hold a dated sighting the backend says so, with the date, in
// `billingNote` — which every one of these screens renders verbatim beneath this
// sentence. The specific claim still reaches the operator; it reaches them from
// the layer that actually knows.
//
// The tier chip and the cost chip beside this sentence already hedge on that
// flag. A hedged chip sitting over a caption that still asserts the meter is
// billing RIGHT NOW is one screen contradicting itself in the same glance, which
// is the whole defect class this module exists to close.
//
// THE DIRECTION NEVER MOVES. An unrefreshed row is not evidence the meter
// stopped, and converting an unverified reading into an all-clear is the
// sign-flipped bug this codebase has now shipped four times. Only the confidence
// is softened, and only on an EXPLICIT false — an absent field reads exactly as
// it did before.
export const ipamPreviewBillingLine = (stillBilling, verified) => {
  if (stillBilling === true) {
    if (verified === false) {
      return 'The pre-flight reports this IPAM still running and still billing, but Prism cannot '
        + 'confirm that from anything current. That does not change until AWS reports it deleted '
        + '— or, for an IPAM Prism adopted, not at all. Treat it as billing.';
    }
    return 'The pre-flight reports this IPAM is still running and still billing right now. That '
      + 'does not change until AWS reports it deleted — or, for an IPAM Prism adopted, not at all.';
  }
  if (stillBilling === false) {
    // AND THIS IS THE DANGEROUS DIRECTION, WHICH IS WHY IT GETS THE SAME AXIS.
    //
    // `stillBilling === false` is reached on TWO degraded paths as well as the
    // honest one: a ledger read that FAILED, and a ledger holding no `ipam` rows
    // at all (services/ipam_service.go, meterVerdict — both set Verified=false and
    // fall through resting on cfg.Status alone). NEITHER involved a reading of the
    // IPAM, which is why the sentence below claims none. `disabled` means A
    // TEARDOWN RAN TO COMPLETION. It does not mean the IPAM was deleted: Prism never deletes an
    // IPAM it adopted, so an adopted IPAM survives a completely successful
    // teardown and goes on metering at Advanced-tier rates.
    //
    // A flat "nothing is billing for this IPAM any more" over that is the
    // sign-flipped bug in its purest form — a live charge hidden behind a read
    // that never landed, stated as settled fact and needing no further action.
    // The direction is still reported, because the reading really does say
    // stopped and inventing the opposite would be the mirror-image defect; what
    // goes away is the claim that it is established, and the operator is pointed
    // at the one place that can settle it.
    if (verified === false) {
      return 'The pre-flight reports nothing is billing for this IPAM any more, but Prism cannot '
        + 'confirm that from anything current. A teardown running to completion is not a deletion '
        + '— Prism never deletes an IPAM it adopted, and an adopted IPAM keeps metering — so check '
        + "the host account's VPC IPAM console before treating this meter as stopped.";
    }
    return 'The pre-flight reports nothing is billing for this IPAM any more.';
  }
  return 'The pre-flight ledger has not been read, so Prism cannot say whether this IPAM is still '
    + 'billing. Treat it as billing until the ledger says otherwise.';
};

// --- THE POOL DELETE VERDICT, AND THE LIST IT IS NOT ALLOWED TO SPEAK FOR -----
//
// `GET /pools/{id}/allocations` is LEDGER ONLY (services/ipam_service.go,
// ListPoolAllocations: "the customer's own allocations live in the
// IpamPoolAllocation cache the refresh owns"). The DELETE, by contrast, probes
// AWS live for EVERY allocation in the pool and refuses on any whose
// AllocationSource is `resource` — including one a member account made for
// itself out of a pool shared to it, which the ledger structurally cannot hold.
//
// So "Nothing is holding this pool. It can be deleted." was a verdict derived
// from a list that cannot contain the blockers, and the pool 409s a moment after
// the screen says it is clear. Each sentence below is scoped to what the list
// actually covers.
export const IPAM_POOL_NO_PRISM_BLOCKERS =
  'No allocation Prism made out of this pool is resource-backed. Allocations member accounts made '
  + 'themselves are not listed here and can still block the delete.';

export const IPAM_POOL_ALLOCATIONS_EMPTY =
  'Prism has not allocated anything out of this pool. Allocations member accounts made themselves '
  + 'out of a pool shared to them are not listed here — the delete still checks AWS for them.';

export const IPAM_POOL_ALLOCATIONS_SUBTITLE =
  'Space Prism handed out of this pool. An allocation AWS made because a real VPC or subnet took '
  + 'the space cannot be released — see the source column. Allocations member accounts made '
  + 'themselves are not listed here, and deleting the pool checks AWS for those too.';

// --- THE CANDIDATE LIST IS NOT A LABEL LOOKUP --------------------------------
//
// `listSecurityHubAccounts` supplies BOTH the display names and the picker's
// candidate rows, and its rejection was swallowed by a bare `catch (_)`. With
// `accounts` emptied, `notEnrolled` is empty, and the picker states "Every
// linked account is enrolled — Nothing left to add" over a read that failed:
// a confident all-clear manufactured from a caught exception, with no error
// anywhere on the page and a select-all checkbox disabled for no stated reason.
export const IPAM_LINKED_ACCOUNTS_UNREADABLE_TITLE =
  "Prism could not read this organization's linked accounts";

export const IPAM_LINKED_ACCOUNTS_UNREADABLE_BODY =
  'So it cannot say what is left to enrol. This is not "every account is already enrolled" — the '
  + 'list of candidates is the read that failed. Account names elsewhere on this page fall back to '
  + 'bare account ids for the same reason.';

// --- "0 WOULD BLOCK TEARDOWN", AND THE THREE WAYS IT IS NOT A ZERO ------------
//
// The Allocations chip counted the loaded, filtered rows and painted itself
// SUCCESS GREEN at zero — including while the pool-allocation cache had never
// been read (the table underneath literally said so), while a cursor remained,
// and while a filter was narrowing the very rows it counted. Its two siblings
// were already fixed: IpamAddressSpace separates "assessed" from "not yet
// assessed" and IpamPublicIPs goes neutral at zero.
export const ipamBlockingAllocationsChip = ({
  blockingCount = 0,
  cacheReadAt = null,
  morePagesRemain = false,
  filtersActive = false,
} = {}) => {
  const reasons = [];
  if (!cacheReadAt) reasons.push('Prism has not completed a read of your pool allocations');
  if (morePagesRemain) reasons.push('more pages of allocations are still unread');
  if (filtersActive) reasons.push('a filter is narrowing the rows being counted');

  if (reasons.length === 0) {
    return {
      label: `${blockingCount} would block teardown`,
      intent: blockingCount > 0 ? 'warning' : 'success',
      hint: 'Resource-backed allocations cannot be released while their resource exists, so each one '
        + 'blocks deleting its pool. Every allocation Prism has read is counted here.',
    };
  }

  return {
    label: blockingCount > 0
      ? `At least ${blockingCount} would block teardown`
      : 'Teardown blockers not assessed',
    intent: blockingCount > 0 ? 'warning' : 'neutral',
    hint: `This is not a complete count: ${reasons.join(', and ')}. A green zero here would read as `
      + '"nothing blocks a teardown", which nothing on this page has established.',
  };
};

// --- POOLS ARE NOT DISCOVERED, BUT THEY ARE RE-READ --------------------------
//
// "they are never discovered, so an empty tree means there are none" is false in
// the way that matters: `refreshPoolLedger` re-reads pools from AWS on every
// refresh and ADOPTS ones Prism did not create. When that read fails only a job
// note records it, so an empty tree can equally mean the pool pass failed —
// and the page had no other state to fall into.
export const IPAM_POOLS_SUBTITLE =
  'The address-space hierarchy: every scope, the pools carved out of it, and the pools carved out '
  + "of those. Pools are created here or adopted from your accounts — an empty tree means Prism's "
  + 'last pool read found none, so run a refresh if you expect an adopted pool here.';

export const IPAM_POOLS_EMPTY_DESCRIPTION =
  'A pool is a block of address space you carve up and hand out. Prism creates pools here and '
  + 'adopts ones it finds in your accounts on every refresh, so an empty tree is either a genuine '
  + "zero or a pool read that did not land — refresh before concluding it is the first. Otherwise: "
  + 'create one in the private scope, provision a CIDR into it, and allocations can be made from it.';

// --- THE POOL TREE IS NOT EVIDENCE THAT AN IPAM EXISTS -----------------------
//
// A tenant with VPC IPAM torn down — no IPAM and no pools left in AWS — was
// served TWO POOLS from a dead test cycle, under the ordinary page chrome, each
// reading "Creating" and "Managed by Prism". The rows were stale ledger rows the
// API still answered with; the page had a not-live card and never reached it,
// because the not-live question was asked INSIDE the empty-tree branch. A
// non-empty stale tree sailed past it and rendered as current.
//
// So the liveness question is asked BEFORE the rows are, and — like every other
// gate in this capability — it has three answers, not two: the capability is
// live, it is DECIDEDLY not live, or PRISM COULD NOT FIND OUT. The third one is
// the one that must not borrow the second's copy: "IPAM is not set up" printed
// over a config row Prism could not read is an absence rendered as a verdict,
// and it points an operator whose IPAM is running and billing at a setup wizard.

// A rejection with no message is still a rejection. `setError(e.message)` alone
// left `error` FALSY for one, and every branch downstream — the not-live card,
// the "No pools yet" zero — is a confident claim about a customer's setup that
// would then be made out of a read that never landed.
export const IPAM_POOLS_READ_FAILED =
  'Prism could not read this customer’s VPC IPAM pool hierarchy, and the service gave no reason. '
  + 'Nothing below is being claimed about what exists in AWS until the read lands.';

// The config read specifically. Same shape as IpamAllocations.js's configUnread
// message, deliberately: the two pages answer the same question the same way.
export const IPAM_POOLS_CONFIG_UNREAD_ERROR =
  'Prism could not read this customer’s VPC IPAM configuration, so it cannot say whether an IPAM '
  + 'exists behind the pools below.';

// The UNKNOWN arm, rendered where the not-live card would otherwise have gone.
// Reached when Prism holds a config row whose status it cannot read — a status
// that is absent or blank is not the string `disabled`, and NOT_LIVE_META's
// fallback would otherwise have dressed it as one.
export const IPAM_POOLS_LIVENESS_UNREAD_TITLE =
  'Prism cannot tell whether VPC IPAM is set up';

export const IPAM_POOLS_LIVENESS_UNREAD_BODY =
  'The configuration row for this customer carries no status, so Prism cannot say whether an IPAM '
  + 'exists, whether setup is still running, or whether it was torn down. That is not the same as '
  + '"not set up" and is not being shown as one. Re-read the configuration; if it keeps coming back '
  + 'without a status, this is a defect rather than a state your customer is in.';

// PAGE-AGNOSTIC ALIASES. The two sentences above are about the CONFIG ROW, not
// about pools — Address space, Allocations and Public IPs need exactly the same
// third answer and were dressing a blank status as "not enabled yet". Exported
// under neutral names so the other four screens do not import a POOLS_ constant
// to say something that has nothing to do with pools.
export const IPAM_LIVENESS_UNREAD_TITLE = IPAM_POOLS_LIVENESS_UNREAD_TITLE;
export const IPAM_LIVENESS_UNREAD_BODY = IPAM_POOLS_LIVENESS_UNREAD_BODY;

// The banner over a tree that IS rendered while liveness is unknown. The rows
// stay on screen — hiding data over an unread field would be its own false
// negative — but they are not presented as current.
export const IPAM_POOLS_UNVERIFIED_TREE_TITLE =
  'These pools have not been confirmed against a live IPAM';

export const IPAM_POOLS_UNVERIFIED_TREE_BODY =
  'Prism could not read this customer’s VPC IPAM configuration, so it cannot confirm the IPAM these '
  + 'pools belong to still exists. They are shown because a pool Prism hides is indistinguishable '
  + 'from a pool you do not have — but treat them as last-known, not as current, until the '
  + 'configuration reads.';

// --- THE "UNSCOPED" ROOT SAYS WHICH KIND OF UNPLACED IT IS -------------------
//
// One label covered two unrelated facts. "No scope resolved" reads as a
// per-pool lookup that missed — a hiccup — which is right when OTHER pools did
// place under a scope. It is wrong, and misleading in the expensive direction,
// when Prism holds NO SCOPES AT ALL: then nothing resolved because there is
// nothing to resolve against, and the honest reading is that these rows are
// left over from an IPAM that is not there any more (or that the scope read
// itself did not land). Both of those are "the ground under this tree is
// missing", not "one lookup missed", so they get their own arm.
export const ipamUnscopedRootChip = ({ scopeCount = 0 } = {}) => {
  if (scopeCount > 0) {
    return {
      label: 'No scope resolved',
      hint: 'Prism could not place these pools under a scope — either the pool’s scope did not '
        + 'resolve, or the pool it was carved out of is no longer in the inventory. They are shown '
        + 'here rather than dropped: a pool missing from this tree is indistinguishable from a pool '
        + 'you do not have, and these ones exist in AWS and occupy quota.',
    };
  }
  return {
    label: 'No IPAM behind these',
    hint: 'Prism holds no scopes for this customer at all, so there is nothing for these pools to '
      + 'hang off. Either they are left over from an IPAM that no longer exists — stale ledger rows '
      + 'rather than address space you have — or the scope read did not land. Prism cannot tell '
      + 'which from here, so it is not claiming either: re-read before acting on them.',
  };
};

export const ipamUnscopedAlert = ({ count = 0, scopeCount = 0 } = {}) => {
  const one = count === 1;
  if (scopeCount > 0) {
    return {
      severity: 'warning',
      title: `${count} ${one ? 'pool could' : 'pools could'} not be placed under a scope`,
      body: 'AWS does not report which scope a pool is in — Prism records it at create time — so a '
        + 'pool created outside Prism, or one whose parent pool has since gone, has nothing to hang '
        + `off. ${one ? 'It is' : 'They are'} shown under Unscoped at the bottom of the tree rather `
        + `than hidden: ${one ? 'it exists' : 'they exist'} in AWS and `
        + `${one ? 'occupies' : 'occupy'} quota.`,
    };
  }
  return {
    severity: 'warning',
    title: `${count} ${one ? 'pool has' : 'pools have'} no scope to belong to`,
    body: 'Prism holds no scopes for this customer at all, so nothing here could be placed. AWS '
      + 'creates the default scopes with the IPAM, which means either the IPAM these '
      + `${one ? 'row belongs' : 'rows belong'} to is gone and ${one ? 'this is a stale ledger row' : 'these are stale ledger rows'} `
      + 'rather than address space you have, or the scope read did not land. Re-read before acting '
      + `on ${one ? 'it' : 'them'} — and do not read ${one ? 'it' : 'them'} as current address space.`,
  };
};

// --- A SCOPE PRISM CANNOT SEE IS SHOWABLE, AND IS NOT A CREATION TARGET ------
//
// LIVE, POC, 2026-08-25. AWS held exactly TWO scopes. The Create pool dialog
// offered FOUR — the two live ones plus two from a previous generation of the
// IPAM, deleted and rebuilt outside Prism — and it listed a DEAD one first, so
// that was the one pre-selected. Submitting produced the backend's refusal:
//
//   "Prism does not know whether scope ipam-scope-0811e1b07074a4efd is public or
//    private and could not read it from AWS; it will not create a pool that might
//    land in the public scope, because that makes the IPAM permanently
//    undeletable: … InvalidIpamScopeId.NotFound"
//
// THAT REFUSAL IS CORRECT AND STAYS. It fails closed, which is the right
// direction. The defect is upstream of it: the operator was handed a
// pre-selected target that cannot work and had to already know to pick a
// different one.
//
// THE DEAD SCOPES ARE NOT FILTERED OUT OF THE SCOPE LIST, AND MUST NOT BE. An
// `observed_missing` row is either already gone — harmless — or an orphan that
// still exists in AWS and still occupies quota, and someone has to be able to
// name it; filtering those rows back out re-creates the billing deadlock the
// backend's ipamLedgerRowIsDead comment was split to fix. The distinction is
// narrower than visibility: a scope Prism cannot see is legitimate to SHOW and
// illegitimate to OFFER AS A CREATION TARGET.
//
// TWO SIGNALS, NOT ONE. `observedMissing` is the completed refresh's verdict —
// and the same UPDATE that sets it BLANKS a pending state
// (markIpamManagedResourcesMissing, services/ipam_service.go), so a dead row's
// `state` frequently reads EMPTY. State alone can therefore never be the test.
// The list below catches the other direction: a row AWS still returns but
// reports as deleting, deleted, never-created, or inside an account that has
// left the organization.
//
// A BLANK OR UNRECOGNISED STATE IS DELIBERATELY NOT ON THE LIST. Prism holds no
// state at all for many adopted scopes, and refusing those would be the
// mirror-image lie — a live scope withheld from the one dialog that needs it.
export const IPAM_SCOPE_UNCREATABLE_STATES = [
  'delete-in-progress',
  'delete-complete',
  'create-failed',
  'isolate-in-progress',
  'isolate-complete',
];

// The short suffix the option carries, and the sentence behind it. The option is
// rendered DISABLED rather than dropped, exactly as the public scope already is:
// an option that simply is not there reads as a Prism bug, while one that is
// there, greyed, and says why reads as a decision.
export const IPAM_SCOPE_MISSING_CREATE_LABEL = 'not seen in AWS — cannot create here';

export const IPAM_SCOPE_MISSING_CREATE_REASON =
  'AWS has stopped returning this scope, so Prism cannot confirm it still exists — or even that it '
  + 'is still the private one. The service will not create a pool it cannot place: it refuses to '
  + 'risk landing one in the public scope, because a single public-scope pool makes the IPAM '
  + 'permanently undeletable. A create against this scope fails. It is still listed because an '
  + 'orphan that may still be occupying quota has to be nameable.';

export const ipamScopeGoneCreateLabel = (state) => `AWS reports "${state}" — cannot create here`;

export const ipamScopeGoneCreateReason = (state) =>
  `AWS reports this scope as "${state}", so there is nothing here to carve a pool out of. It is `
  + 'still listed rather than dropped: a scope Prism hides is indistinguishable from one you do '
  + 'not have.';

// ipamScopeCreateBlock answers ONE question about ONE scope row: may a pool be
// created into it? `null` means yes. Anything else is {label, reason} and the
// option is drawn disabled with the reason on it.
export const ipamScopeCreateBlock = (scope = {}) => {
  if (scope?.observedMissing) {
    return { label: IPAM_SCOPE_MISSING_CREATE_LABEL, reason: IPAM_SCOPE_MISSING_CREATE_REASON };
  }
  const state = String(scope?.state ?? '').trim().toLowerCase();
  if (IPAM_SCOPE_UNCREATABLE_STATES.includes(state)) {
    return { label: ipamScopeGoneCreateLabel(state), reason: ipamScopeGoneCreateReason(state) };
  }
  return null;
};

// The banner over a Scope select that is holding dead options. It exists so the
// limit is understood before any effort is spent on the form, and so the greyed
// rows read as a decision rather than as a glitch.
export const IPAM_SCOPE_DEAD_OPTIONS_TITLE =
  'Some of these scopes are not ones Prism can create into';

export const IPAM_SCOPE_DEAD_OPTIONS_BODY =
  'They are scopes AWS has stopped returning, or reports as gone — most often the previous '
  + 'generation’s ids, left behind when an IPAM is deleted and rebuilt outside Prism. They stay '
  + 'on the list because an orphan that may still be occupying quota has to be nameable, but a pool '
  + 'created into one is refused: Prism cannot even confirm such a scope is private, and one pool in '
  + 'a public scope makes the IPAM permanently undeletable.';

// Why a greyed Create pool button is greyed. The dialog's Scope select is fed
// from `listIpamScopes`, which answers an EMPTY PAGE for a tenant with no IPAM —
// so on an unconfigured tenant the button opened a dialog with an empty required
// field and a submit that could never enable, explaining nothing.
//
// `privateScopeCount` IS THE COUNT OF PRIVATE SCOPES THAT CAN ACTUALLY BE
// CREATED INTO, not the count of private rows held. `deadPrivateScopeCount` is
// the rest, and it earns its own arm: "there is no private scope" is false, and
// misleading, for a customer whose ledger holds two of them from an IPAM that no
// longer exists.
export const ipamCreatePoolDisabledReason = ({
  privateScopeCount = 0,
  deadPrivateScopeCount = 0,
  scopesRead = true,
  submitting = false,
} = {}) => {
  if (submitting) return 'Prism is creating the pool.';
  if (!scopesRead) {
    return 'Prism could not read this IPAM\'s scopes, so it cannot offer one to create the pool in. '
      + 'A pool must be created inside a scope — retry the read first.';
  }
  if (privateScopeCount === 0 && deadPrivateScopeCount > 0) {
    return 'There is no live private scope to create a pool in. Every private scope Prism holds for '
      + 'this customer is one AWS has stopped returning, or reports as gone — usually the previous '
      + 'generation’s ids, left behind when an IPAM is deleted and rebuilt outside Prism. They '
      + 'are still listed, because an orphan that may still be occupying quota has to be nameable, '
      + 'but a pool cannot be created into one. Re-read the scopes; if none comes back live, the '
      + 'IPAM these belong to is gone.';
  }
  if (privateScopeCount === 0) {
    return 'There is no private scope to create a pool in. AWS creates the default private scope '
      + 'with the IPAM, so an IPAM that is not enabled yet has none — and Prism will not create a '
      + 'pool in the public scope, because one public-scope pool makes the IPAM permanently '
      + 'undeletable.';
  }
  return '';
};

// --- WHY THE ADD BUTTON IS OFF, AND IT IS NOT ALWAYS "SOMETHING IS RUNNING" ---
//
// `configAllowsMemberOps` accepts only `enabled` and `partial_failure`, so the
// disabled Add carried "Enrollment changes are paused while the current
// operation finishes" on FOUR statuses — and on two of them nothing is running
// and nothing is going to finish. `awaiting_consent` is a wizard the operator
// abandoned; `blocked` is a teardown parked indefinitely on them.
export const ipamEnrollmentDisabledReason = (status) => {
  switch (status) {
    case 'awaiting_consent':
      return 'Enrollment is off because VPC IPAM setup was never finished — it stopped at the cost '
        + 'estimate, so there is no IPAM to enrol accounts into. Nothing is running. Finish the '
        + 'setup wizard.';
    case 'blocked':
      return 'Enrollment is off because a teardown is parked on you. It stopped part-way and will '
        + 'not resume on its own — the IPAM is still there and still billing until it is dealt '
        + 'with. Clear the teardown in Settings first.';
    case 'in_progress':
      return 'Enrollment changes are paused while the current operation finishes.';
    case 'disabled':
      return 'Enrollment is off because VPC IPAM is not enabled for this customer, so there is no '
        + 'IPAM to enrol accounts into.';
    default:
      return 'Enrollment changes are paused while the current operation finishes.';
  }
};

// --- THE ESTIMATED BILL CARRIES ITS AGE, ON EVERY SCREEN THAT PRINTS IT -------
//
// ipamMeteredCaption already states the rule and carries the age on the
// dashboard. The Settings "Estimated bill" printed the same figure with no age
// at all — and the age is what separates a figure re-counted a minute ago from
// one whose re-count has been failing silently for a fortnight while the refresh
// itself kept reporting success.
export const ipamEstimatedBillAge = (countedRel) =>
  (countedRel
    ? `Counted ${countedRel}; it is re-counted by a full refresh, not live.`
    : 'Prism has no record of when this was counted. It is re-counted by a full refresh, not live.');

// --- `blocked` NO LONGER IMPLIES A BLOCKER --------------------------------------
//
// IPAM_TEARDOWN_BLOCKED used to be a one-meaning status: a job sitting at
// `blocked` was always waiting on a resource the customer's own engineer had to
// go and delete, so every screen could take a non-empty blocker list for granted
// and word itself accordingly.
//
// The teardown's new settle arm (services/ipam_teardown.go, `run.awaitCascade &&
// ipamDeleted`) produces a combination that did not previously exist:
//
//     job status   = blocked          (non-terminal, so the sweep resumes it)
//     config status = partial_failure (re-beginnable, unlike `blocked`)
//     blockers      = EMPTY
//
// and it means the OPPOSITE of the old one: AWS confirmed the IPAM was DELETED
// and the meter stopped, and the pass merely ran out of its graceful budget
// before the member-side cleanup. It is deliberately NOT `awaiting_cascade` —
// there is nothing left to force-delete, and paying that cost (all CIDR address
// history, irreversibly) for nothing is the trap the arm exists to avoid.
//
// So the panel needs a THIRD arm, not a reworded one. The blocker-present
// wording below is the original, character for character; the awaiting-cascade
// wording is the original empty-list wording, also unchanged; the third is new.
// `meterVerdict` is null when the backend has written its own prose about the
// meter, because the config message is better evidence than anything this module
// can assert — and it is the hedge, never a flat "still billing", when there is
// no message at all.
export const ipamTeardownBlockedCopy = ({
  blockerCount = 0,
  awaitingCascade = false,
  configMessage = '',
} = {}) => {
  if (blockerCount > 0) {
    return {
      title: 'Teardown is waiting on resources Prism must not delete.',
      body: 'Every stage that does not depend on the pools below has already run, so the rest of the '
        + 'teardown is not held up by this.',
      meterVerdict: IPAM_STILL_BILLING,
      blockersHold: true,
    };
  }

  if (awaitingCascade) {
    return {
      title: 'The ordered teardown could not finish.',
      body: 'Prism stopped rather than pushing on, and it is waiting for a decision. Nothing further '
        + 'happens until you retry the ordered teardown or force delete the IPAM.',
      meterVerdict: IPAM_STILL_BILLING,
      blockersHold: false,
    };
  }

  return {
    title: 'The ordered teardown stopped before it finished — nothing is blocking it.',
    body: 'No resource is holding a pool and there is nothing for you to delete. This pass ran out '
      + 'of its graceful budget part-way through, and the only thing it needs is Retry teardown. '
      + 'Force delete is not on offer and is not needed: it is accepted only from a teardown that '
      + 'has asked for it, and this one has not.',
    // The backend writes its own prose for this arm and it is the better witness
    // — it is the only thing on the page that knows whether AWS confirmed the
    // delete. With no message, Prism does not know, and says so rather than
    // asserting either direction.
    meterVerdict: configMessage
      ? null
      : 'Prism cannot tell from this panel alone whether this teardown deleted the IPAM, so it '
        + 'cannot say whether the meter has stopped. Treat it as still billing until the pre-flight '
        + 'ledger in Settings says otherwise.',
    blockersHold: false,
  };
};

// The residue banner's headline, which was IPAM_STILL_BILLING and nothing else.
// That is a flat claim about a live meter, and the same new settle arm can now
// raise this banner over an IPAM AWS has confirmed DELETED — at which point the
// headline is the one sentence on the card that is false. The banner may assert
// the meter only when it is naming per-account failures (a teardown that left
// the IPAM up) or when there is no backend prose to defer to.
export const IPAM_RESIDUE_UNFINISHED_TITLE = 'The teardown did not finish.';

export const IPAM_RESIDUE_METER_DEFERRED =
  "Whether the IPAM itself was deleted — and so whether the Advanced-tier meter has stopped — is "
  + "what Prism's own message below reports. The pre-flight ledger in the danger zone re-reads it.";

// ---------------------------------------------------------------------------
// THE IPAM THAT OUTLIVES THE CONFIG ROW
// ---------------------------------------------------------------------------
//
// SIX SCREENS PRINTED "THERE IS NO IPAM" OVER AN IPAM THAT WAS RUNNING AND
// BILLING, AND TWO OF THEM ALSO HID THE REMEDY.
//
// A clean teardown of an ADOPTED IPAM settles `config.status = disabled` — and
// deliberately does not delete the IPAM: services/ipam_teardown.go raises a
// `critical` residue row saying it KEEPS METERING, because DeleteIpam destroys
// the customer's own allocation history and that history was never Prism's. So
// `disabled` on an adopted row means "Prism stopped using it", never "it is
// gone", and the backend's own pre-flight says so in as many words:
//
//   "The IPAM is STILL RUNNING AND STILL BILLING … and it is ADOPTED: Prism did
//    not create it and will never delete it, so Retry teardown cannot stop the
//    meter. Delete the IPAM yourself in the host account to stop it."
//
// The second case is `failed`. ipamPhaseRollup settles `failed` whenever the
// host cell is not `enabled`, which INCLUDES CreateIpam succeeding and a later
// host step failing — with cfg.IpamID already persisted, and with an error
// message that commonly ends "and the IPAM EXISTS and is metering".
//
// --- WHY THIS IS NOT SIMPLY `!!config.ipamId` -------------------------------
//
// ipam_id SURVIVES A SUCCESSFUL TEARDOWN ON PURPOSE (DD-16, services/
// ipam_teardown.go: "ipam_id IS DELIBERATELY LEFT ALONE … it is the only handle
// on anything whose removal may still be outstanding"). On a NON-adopted tenant
// that ran a teardown to completion the id therefore names an IPAM AWS has
// already deleted, and `disabled` is written exactly once — by settleTeardownRun,
// when a teardown COMPLETED (services/ipam_service.go, ipamInventoryIsGone). So
// the id alone would manufacture the mirror-image false alarm on every properly
// torn-down tenant, which is why `disabled` + not adopted is the one combination
// where a held id is read as residue rather than as evidence.
//
// `awaiting_consent` is excluded for the same reason pointing the other way:
// writeDraftConfig's UPDATE deliberately does not touch ipam_id, so a tenant who
// tore an IPAM down and restarted the wizard carries the OLD id at that status
// and no claim can be built on it. `in_progress` and `blocked` are excluded
// because both already say the IPAM is there — `blocked` already carries "still
// there and still billing" verbatim — and overwriting them would drop their own
// remedies.

// No IPAM behind this row that these sentences may speak about.
export const IPAM_RESIDUE_NONE = 'none';
// CERTAIN. The IPAM pre-existed Prism, Prism never deletes one it did not create,
// so it is still there and still metering whatever the status says.
export const IPAM_RESIDUE_ADOPTED = 'adopted';
// EVIDENCE, NOT PROOF. Prism is holding the id of an IPAM it created under a
// status that says setup did not finish. It may still exist and still meter, and
// the copy hedges accordingly rather than asserting either direction.
export const IPAM_RESIDUE_HELD = 'held';

// The IPAM id this config row is still holding, trimmed, or '' when it holds none.
export const ipamHeldIpamId = (config) => {
  const id = typeof config?.ipamId === 'string' ? config.ipamId.trim() : '';
  return id;
};

// `ipamAdopted` marks an IPAM that already existed in the host account and that
// Prism took over rather than created. Nothing ever clears it.
export const ipamIsAdoptedIpam = (config) => config?.ipamAdopted === true;

export const ipamResidualIpam = (config) => {
  if (!ipamHeldIpamId(config)) return IPAM_RESIDUE_NONE;
  const status = config?.status;
  if (status === 'in_progress' || status === 'blocked') return IPAM_RESIDUE_NONE;
  // Adoption is decisive at EVERY remaining status, `awaiting_consent` included:
  // an adopted IPAM was the customer's before Prism touched it and no Prism code
  // path deletes it, so a stale id cannot make this claim wrong the way it can
  // for one Prism created.
  if (ipamIsAdoptedIpam(config)) return IPAM_RESIDUE_ADOPTED;
  if (status === 'failed') return IPAM_RESIDUE_HELD;
  return IPAM_RESIDUE_NONE;
};

// Titles, spelled once. Both are deliberately about the IPAM rather than about
// the capability: the operator's question at this point is "am I paying for
// something", not "is the feature on".
export const IPAM_RESIDUAL_ADOPTED_TITLE = 'This IPAM is still running and still billing';
export const IPAM_RESIDUAL_HELD_TITLE = 'An IPAM was created and Prism is still holding its id';

// What PRISM's side of the story is, per status. Kept separate from the sentences
// about the IPAM itself so the two can never contradict each other: this half is
// about what the screen is not showing, the other half is about what is running.
const residualLead = (status, pageNoun) => {
  switch (status) {
    case 'disabled':
      return `Prism has stopped managing VPC IPAM for this customer, so no ${pageNoun} are shown here.`;
    case 'failed':
      return `VPC IPAM setup did not finish, so Prism is not managing any ${pageNoun} for this customer `
        + 'and nothing will resume on its own.';
    case 'awaiting_consent':
      return `VPC IPAM setup was started and never finished, so Prism is not managing any ${pageNoun} `
        + 'for this customer.';
    default:
      return `VPC IPAM is not running in Prism for this customer, so no ${pageNoun} are shown here.`;
  }
};

const inHostAccount = (config) => {
  const host = typeof config?.hostAccountId === 'string' ? config.hostAccountId.trim() : '';
  return host ? ` in host account ${host}` : ' in the host account';
};

// THE REMEDY, SPELLED ONCE. It appears on the five inventory pages, on Settings
// and now on the dashboard's meter panel, and a remedy that gets retyped is a
// remedy that drifts — this module exists for exactly that failure.
export const ipamDeleteItYourself = (config) =>
  `Delete the IPAM yourself${inHostAccount(config)} to stop it.`;


/**
 * ipamResidualIpamCard — the empty-state card for a not-live config row that
 * Prism can still see an IPAM behind, or null when it cannot see one.
 *
 * `pageNoun` is the plural noun the calling page lists ("pools", "allocations"),
 * used ONLY in the lead clause. Every sentence that makes a claim about the IPAM
 * or the meter is identical on all of them, which is the whole point of spelling
 * them here.
 */
export const ipamResidualIpamCard = (config, pageNoun = 'resources') => {
  const kind = ipamResidualIpam(config);
  if (kind === IPAM_RESIDUE_NONE) return null;
  const id = ipamHeldIpamId(config);
  const host = inHostAccount(config);
  const lead = residualLead(config?.status, pageNoun);

  if (kind === IPAM_RESIDUE_ADOPTED) {
    return {
      title: IPAM_RESIDUAL_ADOPTED_TITLE,
      description: `${lead} The IPAM itself was not deleted: it already existed${host} when Prism took `
        + 'it over, and Prism never deletes an IPAM it did not create. Unless you have deleted it '
        + `yourself since, ${id} is still there and still billing, and no button in Prism can stop `
        + `that meter — Retry teardown cannot either. ${ipamDeleteItYourself(config)}`,
    };
  }

  return {
    title: IPAM_RESIDUAL_HELD_TITLE,
    description: `${lead} Prism is still holding the id of an IPAM it created, ${id}, so an IPAM may `
      + `still exist${host} and still be billing — do not read this screen as "there is no IPAM". `
      + 'Check it in the AWS console, then either re-run the setup wizard to finish enabling it or '
      + 'run a teardown from Settings to delete it and stop the meter.',
  };
};

/** The one-paragraph form, for the screens that state this in an Alert rather
 *  than in an empty-state card. Same sentences, joined. */
export const ipamResidualIpamAlert = (config, pageNoun = 'resources') => {
  const card = ipamResidualIpamCard(config, pageNoun);
  return card ? `${card.title}. ${card.description}` : null;
};

// ---------------------------------------------------------------------------
// THE RESIDUE BANNER'S METER VERDICT — READ, NEVER INFERRED FROM A STRING SHAPE
// ---------------------------------------------------------------------------
//
// `const teardownMeterAsserted = !teardownProse;` inferred a LIVE METER from the
// SHAPE of config.errorMessage: a message that does not parse as the per-account
// map was taken as the backend's own prose about the meter, and a message that
// does parse was taken as proof the IPAM was still up. The second half is false.
// The settle arm that writes a per-account map CO-OCCURS with a successful
// DeleteIpam — a host-side step can fail into run.errs without aborting the stage
// loop, and stage 9/10 checkpoint failures land after the delete — so the banner
// announced "still running and still billing" on the same danger-zone screen
// where ipamPreviewBillingLine said the pre-flight reports nothing is billing.
//
// IpamTeardownPreview.StillBilling exists precisely so no client derives this.
// Three values, and `undefined` is the third: it is spent as a hedge, never as a
// "no" and never as a flat assertion. This is the model ipamTeardownBlockedCopy
// already follows for the panel.
export const IPAM_RESIDUE_METER_UNKNOWN =
  'Prism has not read the pre-flight ledger, so it cannot say whether the IPAM itself was deleted '
  + 'or whether the Advanced-tier meter has stopped. Treat it as still billing until the pre-flight '
  + 'ledger in the danger zone says otherwise.';

export const IPAM_RESIDUE_METER_STILL_BILLING =
  'The pre-flight ledger reports this IPAM is still running and still billing. Advanced-tier charges '
  + 'continue until the IPAM itself is deleted.';

export const IPAM_RESIDUE_METER_STOPPED =
  'The pre-flight ledger reports nothing is billing for this IPAM any more. What is left is the '
  + 'member-side residue below.';

// THE SAME SENTENCE WITH THE CONFIDENCE TAKEN OUT OF IT, for a verdict Prism
// cannot confirm from anything current — a row it has stopped refreshing, a
// ledger read that failed, or a ledger holding no `ipam` row for this tenant at
// all. The sentence names none of the three: two of them had no sighting to
// name, and copy that invents one is the defect it was written to prevent.
//
// This banner is the LOUDEST position on the page, and "nothing is billing any
// more, what is left is member-side residue" reads as "the money question is
// closed, only tidying remains". On the degraded path that sentence sits over an
// ADOPTED IPAM that survived a completely successful teardown and is still
// metering — `disabled` means a teardown COMPLETED, never that the IPAM was
// deleted. Same sign-flipped bug this whole module exists to stop, in the one
// place an operator is most likely to stop reading.
//
// The direction is unchanged: the reading still says stopped. Only "established"
// goes away, and the console — the one thing that can actually settle it — takes
// its place.
// NOTE THE MISSING WORDS. The verified sibling above says "the pre-flight LEDGER
// reports"; this one says only "the pre-flight reports", because the ledger is
// exactly what may not have been read — or may hold nothing at all. The endpoint
// did return this verdict; nothing here claims what it was built from.
export const IPAM_RESIDUE_METER_STOPPED_UNVERIFIED =
  'The pre-flight reports nothing is billing for this IPAM any more, but Prism cannot confirm that '
  + 'from anything current. A teardown running to completion is not a deletion — Prism never '
  + 'deletes an IPAM it adopted, and an adopted IPAM keeps metering — so check the host '
  + "account's VPC IPAM console before treating the meter as stopped. The member-side residue "
  + 'below is what is left either way.';

// And the mirror arm, which is a CONTRADICTION FIX rather than a hidden charge.
// ipamPreviewBillingLine stopped asserting the present tense on an unrefreshed
// reading, and this banner renders ten lines above it on the danger tab — one
// screen shouting "is still running and still billing" over a caption saying
// Prism cannot confirm that is exactly the two-claims-one-meter defect that put
// the StillBilling field on the wire in the first place. The verdict stays
// ASSERTED, and therefore stays red: an unrefreshed ledger is not evidence the
// IPAM is gone, and softening the direction here would hide a live charge.
export const IPAM_RESIDUE_METER_STILL_BILLING_UNVERIFIED =
  'The pre-flight reports this IPAM still running and still billing, but Prism cannot confirm that '
  + 'from anything current. Advanced-tier charges continue until the IPAM itself is deleted.';

/**
 * ipamResidueMeterVerdict — { asserted, title, line } for the residue banner.
 *
 * `stillBilling` is IpamTeardownPreview.StillBilling, passed straight through
 * from the server. `configMessage` is only ever a fallback for the UNREAD case,
 * and even then it is deferred to rather than read: the backend's own prose is a
 * better witness to the meter than anything a client can assert, which is the
 * one part of the old code that was right.
 *
 * `verified` is IpamTeardownPreview.BillingVerified, and it is a SECOND AXIS that
 * never moves the first. It says whether the verdict rests on a current
 * observation or on state Prism has stopped refreshing — or on a ledger read that
 * failed, or on a ledger with no `ipam` rows in it at all. `asserted` therefore
 * stays exactly what `stillBilling` makes it: only the SENTENCE changes.
 *
 * Both hedges fire on an EXPLICIT false. A payload without the field — an older
 * server, or the callers that genuinely have no verdict to qualify — behaves
 * precisely as it did before.
 */
export const ipamResidueMeterVerdict = ({ stillBilling, verified, configMessage = '' } = {}) => {
  if (stillBilling === true) {
    if (verified === false) {
      return {
        asserted: true,
        title: IPAM_STILL_BILLING,
        line: IPAM_RESIDUE_METER_STILL_BILLING_UNVERIFIED,
      };
    }
    return { asserted: true, title: IPAM_STILL_BILLING, line: IPAM_RESIDUE_METER_STILL_BILLING };
  }
  if (stillBilling === false) {
    // NOT the bare IPAM_RESIDUE_METER_STOPPED when the stop is unverified. See the
    // constant: this is the loudest sentence on the page and the one that closes
    // the money question, and the degraded path reaches it over a live meter.
    if (verified === false) {
      return {
        asserted: false,
        title: IPAM_RESIDUE_UNFINISHED_TITLE,
        line: IPAM_RESIDUE_METER_STOPPED_UNVERIFIED,
      };
    }
    return { asserted: false, title: IPAM_RESIDUE_UNFINISHED_TITLE, line: IPAM_RESIDUE_METER_STOPPED };
  }
  return {
    asserted: false,
    title: IPAM_RESIDUE_UNFINISHED_TITLE,
    line: String(configMessage || '').trim() ? IPAM_RESIDUE_METER_DEFERRED : IPAM_RESIDUE_METER_UNKNOWN,
  };
};

// ---------------------------------------------------------------------------
// config.note IS NOT ALWAYS INFORMATION
// ---------------------------------------------------------------------------
//
// One slot, several writers (models/ipam_models.go: "IT HAS SEVERAL WRITERS AND
// ONE SLOT, WHICH IS WHY NoteSource EXISTS"). The estimate's and the consent
// record's notes really are informational. The UNATTRIBUTED ones are the
// DEGRADATION CHANNEL — setConfigNote clears note_source on purpose — and the
// loudest of them is applyIpamDrift's:
//
//   "AWS no longer reports IPAM %s in account %s / %s. Nothing is being
//    discovered and no pool can be allocated from; disable and re-enable VPC
//    IPAM once the account is in a known state."
//
// That was rendered as severity="info" on the dashboard and on no other page —
// including every page still drawing rows the sentence has just declared stale.
export const IPAM_NOTE_DRIFT_TITLE = 'AWS is no longer reporting this IPAM';

export const ipamConfigNoteSeverity = (note, noteSource) => {
  const text = String(note || '').trim();
  if (!text) return null;
  const src = String(noteSource || '').trim();
  // The two ATTRIBUTED writers. Both are statements about an estimate or about a
  // consent record, and neither is a degradation.
  if (src === 'estimate' || src === 'consent') return 'info';
  if (/no longer reports IPAM/i.test(text)) return 'error';
  return 'warning';
};

// The title an unattributed note gets, or null when it needs none.
export const ipamConfigNoteTitle = (note, noteSource) =>
  (ipamConfigNoteSeverity(note, noteSource) === 'error' ? IPAM_NOTE_DRIFT_TITLE : null);

// ---------------------------------------------------------------------------
// THE POOL DETAIL PAGE'S FRESHNESS LINE, AND THE ONE CONTROL THAT WAS MIS-GATED
// ---------------------------------------------------------------------------
//
// EVERY TABLE ON THAT PAGE IS A LEDGER READ, NOT A LIVE POLL. Only the Pool card
// said so. When the customer deletes the IPAM outside Prism, the four tables
// below it went on rendering a dead pool as fully current — "State: Creating",
// "3 CIDRs are in this pool" — with no caveat anywhere near the rows.
export const IPAM_POOL_LEDGER_CAVEAT =
  'From the last refresh Prism ran, not a live poll.';

// And the stronger form, for a pool AWS has stopped returning. `observed_missing`
// is set ONLY by a pass that completed, wrote every row it read and did not come
// back empty (services/ipam_service.go, markIpamManagedResourcesMissing), so it
// means "AWS answered and did not name this" — never "Prism could not look".
export const IPAM_POOL_MISSING_CAVEAT =
  "Prism's last record of this pool, from before AWS stopped returning it — not a live read.";

// AND THE THIRD CASE, WHICH IS NEITHER OF THE OTHER TWO: PRISM HAS NO LIVENESS
// FACT AT ALL.
//
// `observed_missing` is set only by a refresh that COMPLETED. When the customer
// deletes the IPAM outside Prism the refresh cannot complete, so the flag is
// never written — and the config read that would say whether an IPAM still
// exists is exactly the read that did not land. The page's liveness is
// three-valued for that reason ('live' | 'not_live' | 'unread'), and only the
// first two are facts.
//
// With liveness UNREAD the plain caveat is not wrong so much as too quiet: "from
// the last refresh Prism ran" still implies there is a live IPAM behind the rows
// and that the refresh merely lags it. Prism has not established that. The rows
// STAY ON SCREEN — hiding data that cannot be vouched for is the mirror-image
// defect, and the established pattern in this capability is a "last-known, not
// current" caveat (IPAM_POOLS_UNVERIFIED_TREE_BODY says the same thing over the
// pool tree) — but they are not presented as current.
// The wording covers BOTH shapes of "no fact": a config read that failed and one
// that has not come back yet. "Could not be read" would be a claim about a
// failure during the in-flight moment, which is the same class of invention this
// caveat exists to stop.
export const IPAM_POOL_UNVERIFIED_CAVEAT =
  "Prism's last record of this pool, not a live read — Prism has not established that the IPAM "
  + 'behind it still exists, so nothing here is confirmed to be current. Treat it as last-known.';

// Three arms, in order of how much Prism knows. `observedMissing` is evidence a
// completed pass produced and outranks a liveness Prism never established; the
// unread arm is the absence of a fact; the plain line is the ordinary case.
export const ipamPoolLedgerCaveat = (observedMissing = false, ipamLiveness = 'live') => {
  if (observedMissing) return IPAM_POOL_MISSING_CAVEAT;
  if (ipamLiveness === 'unread') return IPAM_POOL_UNVERIFIED_CAVEAT;
  return IPAM_POOL_LEDGER_CAVEAT;
};

// --- EDIT AND DELETE ARE NOT THE SAME QUESTION -------------------------------
//
// They were gated together on `readOnly`, and that hid a real refusal behind a
// dialog. ModifyIpamPool IS in ipamOpsNeedingALiveIpam (services/ipam_service.go),
// so against a dead IPAM the operator fills the whole edit form and gets a 400.
//
// DELETE IS DELIBERATELY NOT GATED and must stay that way. The backend's own
// comment is explicit: DeleteIpamPool, DeleteIpamScope, DeprovisionIpamPoolCidr
// and ReleaseIpamPoolAllocation are the CLEANUP surface, and refusing them "would
// leave a customer account holding resources with no in-product way to remove
// them". The rule, in the backend's words: a dead IPAM must not be GROWN, and
// must always be cleanable.
export const IPAM_POOL_EDIT_NEEDS_LIVE_IPAM =
  'Editing a pool changes something inside the IPAM, and this customer\u2019s IPAM is not live — '
  + 'ModifyIpamPool is refused against one that is gone, so the form would only produce an error. '
  + 'Deleting the pool is still offered: a dead IPAM must not be grown, but it must always be '
  + 'cleanable.';

export const IPAM_POOL_EDIT_READ_ONLY =
  'This pool is read-only in Prism: AWS created it inside the IPAM, or the customer created it and '
  + 'Prism adopted it. Neither is Prism\u2019s to reshape.';

// ---------------------------------------------------------------------------
// THE METER IS A LEDGER FACT. `tier` IS A SETTING.
// ---------------------------------------------------------------------------
//
// The dashboard's header chip read "Advanced tier — billed" off `config.tier`
// ALONE, beside a dollar figure, over an IPAM AWS had confirmed DELETED. `tier`
// and `estimatedActiveIps` are reported verbatim ON PURPOSE — one is a setting
// the customer chose and the other is a timestamped measurement — and neither is
// evidence that a meter is running.
//
// GET /summary and GET /cost-preview now both carry `stillBilling`, derived from
// the LEDGER by the same evidence GetTeardownPreview uses, and `billingNote`, the
// reason in prose. THREE STATES, and the third is the reason this is not a
// boolean: the backend falls back to the status WITH A WARNING rather than
// failing when the ledger read degrades, so an ABSENT `stillBilling` means "not
// established" and is hedged — never spent as "no".
//
// The sentence for each state is ipamPreviewBillingLine's, deliberately: the
// danger zone, the residue banner and this chip must end up making ONE claim
// about one meter, in one spelling.
export const ipamTierChip = ({ tier, stillBilling, adopted = false, verified } = {}) => {
  const advanced = tier === 'advanced';
  if (!advanced) {
    // A free-tier row is not a billing claim, so there is nothing to hedge. There
    // used to be an "EXCEPT when the ledger disagrees with the setting" arm here,
    // returning 'Free tier — the ledger reports a live meter' in warning amber on
    // `stillBilling === true`. IT WAS WRONG THREE TIMES OVER AND IS DELETED:
    //
    //   1. IT FIRED FOR EVERY HEALTHY FREE-TIER TENANT, FOREVER. StillBilling is
    //      `cfg.Status != disabled` (services/ipam_service.go, meterVerdict), so an
    //      ENABLED free-tier IPAM has it true permanently and by construction. The
    //      chip was not detecting a disagreement; it was rendering the fact that
    //      the capability was switched on.
    //   2. THERE IS NO DISAGREEMENT TO REPORT. The backend's own struct doc says a
    //      Free-tier IPAM "stands and bills nothing" — standing IS StillBilling, so
    //      the two facts agree and the chip called that a conflict.
    //   3. THE LABEL PRESUPPOSED A READING. "the ledger reports" over a verdict
    //      derived from the config status is the same unread-state-as-fact defect
    //      Task D took out of every other arm in this file.
    //
    // A permanent warning is worse than no warning: it trains the operator to read
    // amber on this capability as decoration, and the amber that means something —
    // the Advanced-tier arms below — is the one that then goes unread.
    //
    // AND THE FIX IS NOT A `stillBilling === false` SUCCESS ARM. That would be the
    // sign-flipped bug in miniature: green for a free-tier row is an all-clear
    // about a meter this branch has no evidence about either way. Hedging on
    // `verified` does not rescue it either — the common case here is
    // `verified === true`, so the hedge would not fire on the very payloads that
    // produced the false alarm. The honest chip states the setting and stops.
    return { label: 'Free tier', intent: 'neutral' };
  }
  if (stillBilling === false) {
    // A GREEN ALL-CLEAR REQUIRES AN ACTUAL READ. `verified === false` here is the
    // backend's ledger query having FAILED, leaving the verdict resting on config
    // status alone — and `disabled` means "a teardown completed", not "the IPAM was
    // deleted". An adopted IPAM survives that and keeps billing, so this branch
    // could paint a live charge green. It still reports stopped, because that is
    // what the evidence says; it just stops calling it success.
    if (verified === false) {
      return { label: 'Advanced tier — meter reported stopped, not verified', intent: 'warning' };
    }
    return { label: 'Advanced tier — meter stopped', intent: 'success' };
  }
  if (stillBilling === true && adopted) {
    // `verified === false` is IpamSummary.billingVerified: Prism cannot confirm this
    // claim from anything current. The DIRECTION does not change — an unconfirmed
    // meter is not evidence the IPAM is gone — but the chip must not assert in red,
    // in the present tense, a charge Prism cannot stand behind. It would sit directly
    // above a caption hedging that very claim. Only an explicit false hedges, so an
    // absent field reads as before.
    //
    // "not re-checked" was the first spelling and it was wrong: re-checking
    // presupposes a first check, and `Verified=false` is also returned for a ledger
    // that holds no `ipam` row for this tenant at all. "not verified" is the
    // module's suffix for exactly that and claims nothing about how the verdict was
    // reached — the dated sighting, where one exists, arrives in `billingNote`.
    if (verified === false) {
      return { label: 'Advanced tier — may still be billed, not verified', intent: 'warning' };
    }
    return { label: 'Advanced tier — billed, and Prism will never stop it', intent: 'error' };
  }
  if (stillBilling === true) {
    // THIS ARM WAS THE ONE HOLE LEFT, AND IT WAS AN INVERSION. It returned the
    // flat 'Advanced tier — billed' without ever consulting `verified`, while the
    // fall-through immediately below — reached when the payload carries NO verdict
    // at all — already returned the more cautious 'Advanced tier — billed, not
    // verified'. So an explicit "Prism cannot confirm this" rendered MORE
    // confidently than complete silence: the strictly worse evidence produced the
    // stronger claim. Every other arm in this function hedges the same field, which
    // is exactly how this one stayed invisible through three passes — it is the
    // arm with no adoption flag and no stopped verdict to draw the eye.
    //
    // It matters more than it looks: a frozen NON-adopted config is precisely the
    // population that ships `verified === false` while `stillBilling` stays true,
    // and this is the chip that population lands on.
    //
    // THE INTENT STAYS `warning` ON BOTH BRANCHES. The hedge belongs in the words;
    // demoting the colour would soften a live-charge warning, which is the failure
    // mode with the worse bill attached.
    if (verified === false) {
      return { label: 'Advanced tier — billed, not verified', intent: 'warning' };
    }
    return { label: 'Advanced tier — billed', intent: 'warning' };
  }
  // Not established. The label still leans towards billing, because that is the
  // direction an unverified meter has to be treated in, and it says it is
  // unverified rather than asserting it.
  return { label: 'Advanced tier — billed, not verified', intent: 'warning' };
};

// The cost tile's own chip. A figure with no qualifier reads as money accruing
// right now, which is the one thing it must not say once the ledger reports the
// meter stopped.
//
// AND SUCCESS-GREEN IS THE DANGEROUS DIRECTION HERE. `stillBilling === false`
// also arrives on the DEGRADED path: the backend's ledger query fails, it falls
// back to the config status alone rather than failing the request, `disabled` is
// read as "nothing is metering", and `billingVerified` comes back false. But
// `disabled` means A TEARDOWN COMPLETED — not that the IPAM was deleted. Prism
// never deletes an IPAM it adopted (services/ipam_teardown.go raises a `critical`
// residue row saying it KEEPS METERING), so that exact combination painted a
// green "Not accruing — meter stopped" over a live Advanced-tier meter that no
// button on this page can stop. Same sign-flipped bug as everywhere else in this
// module, wearing the one colour an operator reads as "nothing to do here".
//
// The verdict itself is still reported — the evidence says stopped, and inventing
// the opposite would be the mirror-image defect — it just stops being called
// success. Only an EXPLICIT false hedges, so a payload with no `billingVerified`
// field behaves exactly as it did before.
export const ipamCostChip = ({ monthlyCost, stillBilling, verified } = {}) => {
  if (stillBilling === false) {
    if (verified === false) {
      return { label: 'Meter reported stopped, not verified', intent: 'warning' };
    }
    return { label: 'Not accruing — meter stopped', intent: 'success' };
  }
  if (monthlyCost === null || monthlyCost === undefined) {
    return { label: 'Not estimated', intent: 'warning' };
  }
  return { label: 'Prism estimate', intent: 'info' };
};
